import { ForbiddenPage } from '@/shared/components';
import { useAccessControl, useCan } from '@skewb/access-control/react';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

interface RequireAbilityProps {
  action: string;
  subject: string;
  /**
   * Extra `(action, subject)` pairs that also grant access — the guard passes if the
   * primary pair OR any of these is permitted (e.g. `/settings` opens for `read Settings`
   * or `read AuditTrail`). Omit for the common single-pair case.
   */
  anyOf?: { action: string; subject: string }[];
  children: ReactNode;
}

/**
 * Route-level guard: renders children if the ability permits `(action, subject)` (or any
 * pair in `anyOf`), else the 403 ForbiddenPage (UX gating only — the backend enforces the
 * boundary). Shows a loader while the ability hydrates, so a hard refresh doesn't flash
 * 403 before deny-all rules resolve.
 */
export function RequireAbility({ action, subject, anyOf, children }: RequireAbilityProps) {
  const { ability, isLoading } = useAccessControl();
  const allowed =
    useCan(action, subject) || (anyOf?.some((p) => ability.can(p.action, p.subject)) ?? false);

  if (isLoading) {
    return (
      <div role="status" className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  return allowed ? <>{children}</> : <ForbiddenPage />;
}
